/**
 * usePermissions Hook
 * 
 * React hook for checking feature permissions and rate limits.
 * Provides an easy way to gate UI components based on user permissions.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getEffectivePermissions,
  checkFeatureAccess,
  checkRateLimit,
  getPlatformSetting,
  isUserApproved,
  clearPermissionCache,
  type EffectivePermission,
  type RateLimitResult,
} from '../services/permissionService';

interface UsePermissionsResult {
  // Loading state
  loading: boolean;
  
  // User approval status
  isApproved: boolean;
  
  // All permissions
  permissions: EffectivePermission[];
  
  // Check specific feature access
  hasAccess: (featureKey: string) => boolean;
  
  // Get config for a feature
  getFeatureConfig: <T extends Record<string, any>>(featureKey: string) => T;
  
  // Check rate limit for an action
  checkLimit: (actionType: string) => Promise<RateLimitResult>;
  
  // Refresh permissions
  refresh: () => Promise<void>;
}

/**
 * Hook for checking user permissions
 */
export function usePermissions(): UsePermissionsResult {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isApproved, setIsApproved] = useState(false);
  const [permissions, setPermissions] = useState<EffectivePermission[]>([]);

  // Load permissions
  const loadPermissions = useCallback(async () => {
    if (!user?.id) {
      setPermissions([]);
      setIsApproved(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [perms, approved] = await Promise.all([
        getEffectivePermissions(user.id),
        isUserApproved(user.id),
      ]);
      setPermissions(perms);
      setIsApproved(approved);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
      setIsApproved(false);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Check if user has access to a feature
  const hasAccess = useCallback((featureKey: string): boolean => {
    // If not approved, no access (unless admin - handled by isApproved)
    if (!isApproved) return false;

    // Check for explicit permission
    const perm = permissions.find(p => p.feature_key === featureKey);
    if (perm) {
      return perm.can_access;
    }

    // SECURITY: Deny by default if no explicit permission exists
    // Admin/super_admin full access should be configured via Permission Center
    // by granting all features to the admin user_type, not by defaulting to allow
    return false;
  }, [permissions, isApproved]);

  // Get feature config
  const getFeatureConfig = useCallback(<T extends Record<string, any>>(featureKey: string): T => {
    const perm = permissions.find(p => p.feature_key === featureKey);
    return (perm?.config || {}) as T;
  }, [permissions]);

  // Check rate limit
  const checkLimit = useCallback(async (actionType: string): Promise<RateLimitResult> => {
    if (!user?.id) {
      return { allowed: false, remaining: 0, reset_at: null };
    }
    return checkRateLimit(user.id, actionType);
  }, [user?.id]);

  // Refresh permissions
  const refresh = useCallback(async () => {
    if (user?.id) {
      clearPermissionCache(user.id);
    }
    await loadPermissions();
  }, [user?.id, loadPermissions]);

  return {
    loading,
    isApproved,
    permissions,
    hasAccess,
    getFeatureConfig,
    checkLimit,
    refresh,
  };
}

/**
 * Hook for checking a single feature's permission
 */
export function useFeatureAccess(featureKey: string): {
  loading: boolean;
  hasAccess: boolean;
  config: Record<string, any>;
} {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [config, setConfig] = useState<Record<string, any>>({});

  useEffect(() => {
    async function check() {
      if (!user?.id) {
        setHasAccess(false);
        setConfig({});
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const [result, approved] = await Promise.all([
          checkFeatureAccess(user.id, featureKey),
          isUserApproved(user.id),
        ]);

        // SECURITY: Only grant access if user is approved AND has explicit permission
        // Deny by default if no permission exists for this feature
        // Admin/super_admin full access should be configured in Permission Center
        const accessGranted = approved && result.hasAccess;

        setHasAccess(accessGranted);
        setConfig(result.config);
      } catch (error) {
        console.error('Error checking feature access:', error);
        setHasAccess(false);
        setConfig({});
      } finally {
        setLoading(false);
      }
    }

    check();
  }, [user?.id, featureKey]);

  return { loading, hasAccess, config };
}

/**
 * Hook for getting a platform setting
 */
export function usePlatformSetting<T>(settingKey: string, defaultValue: T): {
  loading: boolean;
  value: T;
  refresh: () => Promise<void>;
} {
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState<T>(defaultValue);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const setting = await getPlatformSetting<T>(settingKey);
      setValue(setting ?? defaultValue);
    } catch (error) {
      console.error('Error loading platform setting:', error);
      setValue(defaultValue);
    } finally {
      setLoading(false);
    }
  }, [settingKey, defaultValue]);

  useEffect(() => {
    load();
  }, [load]);

  return { loading, value, refresh: load };
}

/**
 * Hook for checking multiple features at once
 */
export function useMultipleFeatureAccess(featureKeys: string[]): {
  loading: boolean;
  access: Record<string, { hasAccess: boolean; config: Record<string, any> }>;
} {
  const { permissions, loading, isApproved } = usePermissions();

  const access = useMemo(() => {
    return featureKeys.reduce((acc, key) => {
      const perm = permissions.find(p => p.feature_key === key);
      acc[key] = {
        hasAccess: isApproved && (perm?.can_access ?? false),
        config: perm?.config || {},
      };
      return acc;
    }, {} as Record<string, { hasAccess: boolean; config: Record<string, any> }>);
  }, [permissions, featureKeys, isApproved]);

  return { loading, access };
}

/**
 * Higher-order component for feature-gating
 */
export function withFeatureAccess<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  featureKey: string,
  FallbackComponent?: React.ComponentType
): React.FC<P> {
  return function FeatureGatedComponent(props: P) {
    const { loading, hasAccess } = useFeatureAccess(featureKey);

    if (loading) {
      return null; // Or a loading spinner
    }

    if (!hasAccess) {
      if (FallbackComponent) {
        return <FallbackComponent />;
      }
      return null;
    }

    return <WrappedComponent {...props} />;
  };
}

export default usePermissions;

